import {
  CreateChatRequestSchema,
  CreateChatResponseSchema,
  CreateWorkstreamRequestSchema,
  CreateWorkstreamResponseSchema,
  DeleteArtifactResponseSchema,
  DeleteWorkstreamResponseSchema,
  GetArtifactResponseSchema,
  ListArtifactsResponseSchema,
  ListMessagesResponseSchema,
  ListWorkstreamsResponseSchema,
  ProblemDetailsSchema,
  WorkstreamChatPollRequestSchema,
  WorkstreamChatPollResponseSchema,
  WorkstreamChatRequestSchema,
  WorkstreamChatSubmitResponseSchema,
  type CreateChatRequest,
  type CreateChatResponse,
  type CreateWorkstreamRequest,
  type CreateWorkstreamResponse,
  type DeleteArtifactResponse,
  type DeleteWorkstreamResponse,
  type GetArtifactResponse,
  type ListArtifactsResponse,
  type ListMessagesResponse,
  type ListWorkstreamsResponse,
  type Message,
  type ProblemDetails,
  type TurnEvent,
  type WorkstreamChatPollRequest,
  type WorkstreamChatPollResponse,
  type WorkstreamChatRequest,
  type WorkstreamChatSubmitResponse,
  type WorkstreamTurnResult,
} from '@agentisai/shared-types';
import { config } from '../config';

const API_PREFIX = '/api/v1';

/** Typed error thrown on any non-2xx response. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

interface RequestOptions {
  signal?: AbortSignal;
  getAccessToken: () => Promise<string>;
}

/**
 * Read a JSON body defensively. An infra-layer error page (an API Gateway /
 * CloudFront timeout, a WAF block) can be non-JSON (HTML / plain text / empty), so
 * `response.json()` throws a `SyntaxError` — return `undefined` so the caller falls
 * back to a status-derived problem. A genuine cancellation (`AbortError`) or a
 * mid-body network drop (`TypeError`) is NOT a "body isn't JSON" case, so it
 * propagates unchanged — a cancelled request stays a cancellation, never a spurious
 * contract error.
 */
async function readJsonBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch (err) {
    if (err instanceof SyntaxError) return undefined;
    throw err;
  }
}

/**
 * Coerce a non-2xx response into a {@link ProblemDetails}. The BFF's own errors are
 * RFC-9457 problem documents and pass through verbatim; an infra-layer error (a bare
 * `{ message }` gateway body, HTML, or an empty body on a Lambda / API-Gateway
 * timeout) is NOT problem+json, so we synthesise one from the status. This keeps
 * every non-2xx classified as an {@link ApiError} — so `describeError` renders a
 * truthful server / auth / rate-limit message — instead of letting
 * `ProblemDetailsSchema.parse` throw a `ZodError`, which `describeError` would
 * mislabel as the input-validation line "Please enter a message before sending."
 * (the exact confusing symptom of a BFF→agent→Bedrock timeout).
 */
function problemForStatus(status: number, raw: unknown): ProblemDetails {
  const parsed = ProblemDetailsSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  return {
    type: 'about:blank',
    title: status >= 500 ? 'Server error' : 'Request error',
    status,
    detail: '',
    instance: '',
  };
}

/**
 * Interpret a fetched response uniformly (shared by the POST helpers and the GET
 * `listWorkstreams`): a non-2xx throws an {@link ApiError} — the BFF's problem+json,
 * or a status-derived synthetic when an infra-layer error isn't problem+json; a 2xx
 * is parsed through `parse`, and a body that drifts from the contract becomes a
 * generic error, never the request-validation ZodError path.
 */
async function handleResponse<T>(response: Response, parse: (raw: unknown) => T): Promise<T> {
  const raw: unknown = await readJsonBody(response);

  if (!response.ok) {
    throw new ApiError(response.status, problemForStatus(response.status, raw));
  }

  try {
    return parse(raw);
  } catch (cause) {
    // A 2xx whose body doesn't match the contract is a server / deploy drift, not
    // user input — surface a generic error (describeError's fallback → GENERIC),
    // never the request-validation ZodError path ("enter a message before sending").
    throw new Error('The server sent an unexpected response.', { cause });
  }
}

async function request<TBody, TResponse>(
  path: string,
  body: TBody,
  parseResponse: (raw: unknown) => TResponse,
  { signal, getAccessToken }: RequestOptions,
): Promise<TResponse> {
  const token = await getAccessToken();

  const response = await fetch(`${config.apiBaseUrl}${API_PREFIX}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  return handleResponse(response, parseResponse);
}

export function sendChatTurn(
  body: CreateChatRequest,
  options: RequestOptions,
): Promise<CreateChatResponse> {
  const validated = CreateChatRequestSchema.parse(body);
  return request(
    '/workstream/create/chat',
    validated,
    (raw) => CreateChatResponseSchema.parse(raw),
    options,
  );
}

export function createWorkstream(
  body: CreateWorkstreamRequest,
  options: RequestOptions,
): Promise<CreateWorkstreamResponse> {
  const validated = CreateWorkstreamRequestSchema.parse(body);
  return request(
    '/workstream/create',
    validated,
    (raw) => CreateWorkstreamResponseSchema.parse(raw),
    options,
  );
}

/**
 * `POST /api/v1/workstream/chat` — submit a chat turn inside an existing
 * workstream (async, ADR-0037): returns `202` with the `turnId` to poll.
 * `focusId` is the constant `'default'` implicit focus until multi-focus
 * switching lands; `agentName`, when present, is the "invite agent" hint
 * forwarded to the Librarian pipeline.
 */
export function submitWorkstreamChatTurn(
  body: WorkstreamChatRequest,
  options: RequestOptions,
): Promise<WorkstreamChatSubmitResponse> {
  const validated = WorkstreamChatRequestSchema.parse(body);
  return request(
    '/workstream/chat',
    validated,
    (raw) => WorkstreamChatSubmitResponseSchema.parse(raw),
    options,
  );
}

/** `POST /api/v1/workstream/chat/poll` — read an async turn's status (ADR-0037). */
export function pollWorkstreamChatTurn(
  body: WorkstreamChatPollRequest,
  options: RequestOptions,
): Promise<WorkstreamChatPollResponse> {
  const validated = WorkstreamChatPollRequestSchema.parse(body);
  return request(
    '/workstream/chat/poll',
    validated,
    (raw) => WorkstreamChatPollResponseSchema.parse(raw),
    options,
  );
}

/**
 * Poll cadence for an in-flight turn — cheap (one DynamoDB read behind the
 * BFF). Env-tunable solely so the unit suite isn't 2 s slower per turn.
 */
const TURN_POLL_INTERVAL_MS = Number(import.meta.env['VITE_TURN_POLL_INTERVAL_MS'] ?? 2_000);
/**
 * Client-side ceiling on a turn — the backstop for a turn that died without a
 * record. Matched to the agent Lambda's 15-min timeout + async event max-age
 * (workstreamagent-stack.ts) so the UI waits out the full server budget for a
 * long staged/tool turn instead of abandoning one the agent is still running.
 */
const TURN_POLL_TIMEOUT_MS = 15 * 60_000;

/**
 * One full async chat turn (ADR-0037): submit, then poll every
 * {@link TURN_POLL_INTERVAL_MS} until the agent completes (minutes-long turns
 * are the point — the old sync request died at the ~29 s gateway budget).
 * Resolves with the server-stamped user message (from the submit ack) and the
 * completed turn result; throws on a failed turn or on timeout.
 * `onProgress` (ADR-0042) fires with the cumulative checkpoint trail each
 * time a poll carries one, so the UI narrates the turn while it runs.
 */
export async function runWorkstreamChatTurn(
  body: WorkstreamChatRequest,
  options: RequestOptions,
  onProgress?: (events: readonly TurnEvent[]) => void,
): Promise<{ userMessage: Message; result: WorkstreamTurnResult }> {
  const submitted = await submitWorkstreamChatTurn(body, options);
  const { turnId, userMessage } = submitted.data;

  const deadline = Date.now() + TURN_POLL_TIMEOUT_MS;
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, TURN_POLL_INTERVAL_MS));
    const poll = await pollWorkstreamChatTurn({ workstreamId: body.workstreamId, turnId }, options);
    if (poll.data.events && poll.data.events.length > 0) onProgress?.(poll.data.events);
    if (poll.data.status === 'complete' && poll.data.result) {
      return { userMessage, result: poll.data.result };
    }
    if (poll.data.status === 'failed') {
      throw new Error('The agents could not complete this turn. Please try again.');
    }
    if (Date.now() >= deadline) {
      throw new Error('This turn is taking too long — please try again.');
    }
  }
}

/**
 * `GET /api/v1/workstream` — the caller's workstreams (S-C1).
 *
 * Parsed through `ListWorkstreamsResponseSchema` (A2 / ADR-0033) so a server
 * shape drift surfaces at the boundary instead of nullable-everything at
 * render. Non-2xx throws `ApiError` for the existing error sink to route.
 */
export async function listWorkstreams(options: RequestOptions): Promise<ListWorkstreamsResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(`${config.apiBaseUrl}${API_PREFIX}/workstream`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    signal: options.signal,
  });

  return handleResponse(response, (raw) => ListWorkstreamsResponseSchema.parse(raw));
}

/**
 * List one workstream's artifacts (Drop 6 / S09) — `GET /workstreams/{id}/artifacts`.
 * Mirrors {@link listWorkstreams}: bearer-auth, forwards the query `signal` so an
 * abandoned navigation / superseded fetch cancels, and validates the tolerant-out
 * envelope. The workstream id is a uuid but is encodeURIComponent'd defensively.
 */
export async function listArtifacts(
  workstreamId: string,
  options: RequestOptions,
): Promise<ListArtifactsResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(workstreamId)}/artifacts`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => ListArtifactsResponseSchema.parse(raw));
}

/**
 * List one workstream's persisted transcript (Drop 7 / S05, ADR-0047) —
 * `GET /workstreams/{id}/messages`. Mirrors {@link listArtifacts}: bearer-auth,
 * forwards the query `signal`, validates the tolerant-out envelope. Seeds the
 * client-held chat on reopen; an empty list is a valid (never-run/aged-out) state.
 */
export async function listMessages(
  workstreamId: string,
  options: RequestOptions,
): Promise<ListMessagesResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(workstreamId)}/messages`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => ListMessagesResponseSchema.parse(raw));
}

/**
 * Fetch one artifact for viewing (Drop 6 / S11) —
 * `GET /workstreams/{id}/artifacts/{artifactId}`. Returns the file's metadata plus,
 * for text-family types under a size cap, the full `content` for inline rendering,
 * and a freshly-presigned `url` for download / binary. A 404 (unknown/deleted id)
 * throws {@link ApiError} with `status: 404` — the View pane self-heals on it.
 * Both path segments are `encodeURIComponent`'d defensively.
 */
export async function getArtifact(
  workstreamId: string,
  artifactId: string,
  options: RequestOptions,
): Promise<GetArtifactResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(
      workstreamId,
    )}/artifacts/${encodeURIComponent(artifactId)}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => GetArtifactResponseSchema.parse(raw));
}

/**
 * Hard-delete one artifact (Drop 6 / S10) —
 * `DELETE /workstreams/{id}/artifacts/{artifactId}`. The BFF is the sole tenant
 * gate (ownership check) and the delete is idempotent server-side (an unknown id
 * is a no-op success), so a 2xx always means "gone"; a non-2xx is a genuine
 * failure and throws {@link ApiError} for the caller to surface. Both path
 * segments are `encodeURIComponent`'d defensively (the id is an opaque uuid).
 */
export async function deleteArtifact(
  workstreamId: string,
  artifactId: string,
  options: RequestOptions,
): Promise<DeleteArtifactResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(
      workstreamId,
    )}/artifacts/${encodeURIComponent(artifactId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => DeleteArtifactResponseSchema.parse(raw));
}

/**
 * Hard-delete a whole workstream (its agent state + chat + every S3 artifact,
 * then the BFF row) — `DELETE /workstreams/{id}`. The BFF is the sole tenant
 * gate (ownership check) and orders the delete agent-first / BFF-row-last, so a
 * 2xx means the workstream is fully gone; a non-2xx is a genuine failure (the
 * workstream is left intact + retryable) and throws {@link ApiError} for the
 * caller to surface. The id is a uuid but is `encodeURIComponent`'d defensively.
 */
export async function deleteWorkstream(
  workstreamId: string,
  options: RequestOptions,
): Promise<DeleteWorkstreamResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(workstreamId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => DeleteWorkstreamResponseSchema.parse(raw));
}
